import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Package, Truck, Home as HomeIcon, Search } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";

const field = "w-full border border-border bg-background px-4 py-3 font-body text-sm focus:outline-none focus:border-foreground transition-colors";
const STEPS = [
  { key: "processing", label: "Confirmed", Icon: Check },
  { key: "packed", label: "Packed", Icon: Package },
  { key: "shipped", label: "Shipped", Icon: Truck },
  { key: "delivered", label: "Delivered", Icon: HomeIcon },
];

export default function Track() {
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    if (e) e.preventDefault();
    if (!id.trim() || !phone.trim()) { setError("Please enter tracking ID and phone number"); return; }
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/track/${id.trim()}`, { params: { phone: phone.trim() } });
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail || "No order found");
    } finally { setLoading(false); }
  };

  const currentIndex = order ? Math.max(0, STEPS.findIndex((s) => s.key === (order.fulfillment || "processing"))) : 0;

  return (
    <div className="pt-32 pb-28 max-w-[900px] mx-auto px-6 md:px-12">
      <Reveal>
        <p className="overline text-[hsl(var(--primary))] mb-6">Order Tracking</p>
        <h1 className="font-display text-5xl md:text-7xl">Track your order</h1>
      </Reveal>

      <form onSubmit={search} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mt-12" data-testid="track-form">
        <input className={field} placeholder="Tracking ID (e.g. NRU7X2K9P)" value={id} onChange={(e) => setId(e.target.value)} data-testid="track-id-input" />
        <input className={field} placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="track-phone-input" />
        <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors" data-testid="track-submit">
          <Search className="w-4 h-4" /> {loading ? "…" : "Track"}
        </button>
      </form>
      {error && <p className="font-body text-sm text-[hsl(var(--primary))] mt-4" data-testid="track-error">{error}</p>}

      {order && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-12 border border-border bg-card p-8" data-testid="track-result">
          <div className="flex flex-wrap justify-between gap-4 border-b border-border pb-6">
            <div><p className="overline text-muted-foreground">Tracking ID</p><p className="font-display text-2xl">{order.tracking_id}</p></div>
            <div><p className="overline text-muted-foreground">Customer</p><p className="font-body">{order.customer_name}</p></div>
            <div><p className="overline text-muted-foreground">Total</p><p className="font-body font-600">₹{order.total}</p></div>
            <div><p className="overline text-muted-foreground">Payment</p><p className="font-body capitalize">{order.payment_method} · {order.status}</p></div>
          </div>

          <div className="flex justify-between mt-10 relative">
            <div className="absolute top-5 left-6 right-6 h-px bg-border" />
            <div className="absolute top-5 left-6 h-px bg-[hsl(var(--primary))] transition-all duration-700" style={{ width: `calc((100% - 3rem) * ${currentIndex / (STEPS.length - 1)})` }} />
            {STEPS.map((s, i) => {
              const active = i <= currentIndex;
              return (
                <div key={s.key} className="relative z-10 flex flex-col items-center gap-2 flex-1">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center border ${active ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-background text-muted-foreground border-border"}`}>
                    <s.Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className={`overline ${active ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-10 border-t border-border pt-6 space-y-2">
            {order.items?.map((it, i) => (
              <div key={i} className="flex justify-between font-body text-sm">
                <span>{it.name} {it.size ? `· ${it.size}` : ""} × {it.qty}</span>
                <span>₹{it.price * it.qty}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
